/**
 * Shared date helpers for Luxon-based date formatting.
 * Replaces moment format tokens that have no direct Luxon equivalent.
 */
import { DateTime } from 'luxon';

/**
 * Returns the ordinal suffix for a day number.
 * e.g. ordinalSuffix(1) → '1st', ordinalSuffix(11) → '11th', ordinalSuffix(21) → '21st'
 * Mirrors moment's 'Do' token.
 */
export function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Formats a DateTime as "DayOfWeek Month DDth, YYYY hh:mm AM/PM"
 * e.g. "Sunday September 8th, 2024 08:20 PM"
 * Equivalent to moment's 'dddd MMMM Do, YYYY hh:mm A'
 */
export function formatLongDate(dt: DateTime): string {
  const time = dt.toFormat('hh:mm a').replace('am', 'AM').replace('pm', 'PM');
  return `${dt.toFormat('cccc LLLL')} ${ordinalSuffix(dt.day)}, ${dt.toFormat('yyyy')} ${time}`;
}

/**
 * Formats a DateTime as "MMM Do" — e.g. "Mar 21st", "Oct 3rd".
 * Equivalent to moment's 'MMM Do' token used by NBA/MLB/NHL week-range labels.
 */
export function formatMonthOrdinalDay(dt: DateTime): string {
  return `${dt.toFormat('MMM')} ${ordinalSuffix(dt.day)}`;
}
